<script setup lang="ts">
import { computed } from 'vue';
import { UiInputField, UiNumberField, UiSelect, UiTextarea, UiText } from '@shopexperts/shared';
import type { Searchability } from '@shopexperts/shared';

const name = defineModel<string>('name', { required: true });
const label = defineModel<string>('label', { required: true });
const description = defineModel<string>('description', { required: true });
const searchability = defineModel<Searchability>('searchability', { required: true });
const groupLabel = defineModel<string>('groupLabel', { required: true });
const groupViewAllLabel = defineModel<string>('groupViewAllLabel', { required: true });
const groupViewAllHref = defineModel<string>('groupViewAllHref', { required: true });
const searchSortIndex = defineModel<number | null>('searchSortIndex', { required: true });

const searchSortIndexValue = computed({
  get: () => searchSortIndex.value == null ? '' : String(searchSortIndex.value),
  set: (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      searchSortIndex.value = null;
      return;
    }
    const parsed = Number(trimmed);
    searchSortIndex.value = Number.isFinite(parsed) ? parsed : null;
  },
});
</script>

<template>
  <div class="border-b border-se-gray-200 px-6 py-5">
    <div class="mb-4">
      <UiText as="h2" variant="h5" text="Schema settings" />
      <UiText as="p" variant="p-small" class="mt-1 text-se-gray-700" text="Define the schema identity and search behavior." />
    </div>

    <div class="grid gap-4 xl:grid-cols-2">
      <div class="flex flex-col gap-1">
        <UiText as="label" variant="label" text="Schema handle" />
        <UiInputField v-model="name" placeholder="partners" />
      </div>
      <div class="flex flex-col gap-1">
        <UiText as="label" variant="label" text="Display label" />
        <UiInputField v-model="label" placeholder="Apps & Themes" />
      </div>
      <div class="flex flex-col gap-1 xl:col-span-2">
        <UiText as="label" variant="label" text="Description" />
        <UiTextarea v-model="description" placeholder="Describe this schema" :rows="3" />
      </div>
      <div class="flex flex-col gap-1">
        <UiText as="label" variant="label" text="Searchability" />
        <UiSelect
          v-model="searchability"
          :options="[
            { label: 'Non-searchable', value: 'non-searchable' },
            { label: 'Searchable', value: 'searchable' },
          ]"
        />
      </div>
      <div class="flex flex-col gap-1">
        <UiText as="label" variant="label" text="Search group label" />
        <UiInputField v-model="groupLabel" placeholder="Search group label" />
      </div>
      <div class="flex flex-col gap-1">
        <UiText as="label" variant="label" text="View all label" />
        <UiInputField v-model="groupViewAllLabel" placeholder="View all label" />
      </div>
      <div class="flex flex-col gap-1">
        <UiText as="label" variant="label" text="View all URL" />
        <UiInputField v-model="groupViewAllHref" placeholder="View all URL" />
      </div>
      <div class="flex flex-col gap-1">
        <UiText as="label" variant="label" text="Search sort index" />
        <UiNumberField v-model="searchSortIndexValue" placeholder="1" :min="0" />
      </div>
    </div>
  </div>
</template>
